import {
  Conversation,
  File,
  FileFolderPath,
  Folder,
  Resource,
} from '@nextforce/api'
import { Button, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { FileFolderSortOption } from 'file_folder_repository/types'
import { Breadcrumbs } from 'file_folder_repository/views/breadcrumb'
import {
  FilterItem,
  FiltersRow,
} from 'file_folder_repository/views/filters_row'
import {
  FileListItem,
  FolderListItem,
} from 'file_folder_repository/views/list_item'
import { useCallback, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'

interface Props {
  conversation: Conversation | undefined
  chatGroupFileFolderList: Resource[] | undefined
  breadcrumbs: FileFolderPath[]
  loading: boolean
  onPressBreadcrumb(index: number): void
  onPressFolder(folder: Folder): void
  onPressFile(file: File): void

  onLongPressResource(resource: Resource): void

  selectedFilterIndex: number
  setSelectedFilterIndex(index: number): void
  filterItems: FilterItem[]
  onPressSearchBarButton(): void
  sortOption: FileFolderSortOption
}

export function SharedFileFoldersView({
  breadcrumbs,
  chatGroupFileFolderList,
  loading,
  filterItems,
  selectedFilterIndex,
  sortOption,
  onPressSearchBarButton,
  setSelectedFilterIndex,
  onPressBreadcrumb,
  onPressFolder,
  onPressFile,
  onLongPressResource,
}: Props) {
  const styles = useStyles()

  return (
    <View style={styles.container}>
      <View style={styles.searchBarContainer}>
        <FiltersRow
          items={filterItems}
          selectedIndex={selectedFilterIndex}
          onSelect={setSelectedFilterIndex}
        />
      </View>
      <View style={styles.breadcrumbsContainer}>
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          onPressBreadcrumb={onPressBreadcrumb}
        />
      </View>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size={'large'}
            color={styles.loadingIndicator.color}
          />
        </View>
      ) : (
        <FlatList
          data={chatGroupFileFolderList}
          keyExtractor={item => item.id + item.name}
          renderItem={({ item }) =>
            item.type === 'file' ? (
              <FileListItem
                key={item.id + item.name}
                file={item}
                onPress={onPressFile}
                onLongPress={onLongPressResource}
                metaDataType={sortOption}
              />
            ) : (
              <FolderListItem
                key={item.id + item.name}
                folder={item}
                onPress={onPressFolder}
                onLongPress={onLongPressResource}
                metaDataType={sortOption}
              />
            )
          }
          ListEmptyComponent={() => (
            <View style={styles.emptyComponent}>
              <Text style={styles.emptyComponentText}>
                {t('noFilesOrFoldersFound')}
              </Text>
            </View>
          )}
          contentContainerStyle={styles.fileFolderListContainer}
        />
      )}
    </View>
  )
}

const PasteButton = ({
  onPress,
  onPressCancel,
}: {
  onPress(): Promise<void>
  onPressCancel(): void
}) => {
  const styles = useStyles()
  const [loading, setLoading] = useState(false)
  const onPressPaste = useCallback(async () => {
    setLoading(true)
    await onPress()
    setLoading(false)
  }, [onPress])

  return (
    <View style={styles.pasteComponentContainer}>
      <Button
        title={t('pasteHere')}
        onPress={onPressPaste}
        loading={loading}
        containerStyle={styles.pasteButtonContainer}
        buttonStyle={styles.pasteButton}
      />
      <Button
        onPress={onPressCancel}
        icon={{ name: 'cancel', color: 'white' }}
        buttonStyle={styles.cancelButton}
        containerStyle={styles.cancelButton}
      />
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  searchBarContainer: { marginHorizontal: 10, marginTop: 10 },
  breadcrumbsContainer: { padding: 10 },
  fileFolderListContainer: {
    paddingBottom: 100,
    flexGrow: 1,
  },
  pasteComponentContainer: {
    padding: 10,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  pasteButtonContainer: {
    borderRadius: 20,
    backgroundColor: theme.colors.primary,
    flex: 1,
    marginRight: 10,
    justifyContent: 'center',
  },
  loadingIndicator: {
    color: theme.colors.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyComponent: { flex: 1, justifyContent: 'center' },
  emptyComponentText: { textAlign: 'center' },
  pasteButton: { flex: 1 },
  cancelButton: {
    borderRadius: 50,
  },
}))
